// components/public/PlaybookThankYou.tsx
// Thank-you / next-step block shown after a successful Playbook signup
// (Priority 241). Replaces LeadCapture's former dead-end success state,
// which said "you're on the list" and stopped — no next step, no CTA.
//
// Me-too before so-what (Aaron lock 2026-09-28, carried from Priority 125):
// confirm the signup and set honest expectations first — nothing is
// emailed today, so this does not claim inbox delivery — then offer one
// clear primary next step. Revenue plan 2026-10-06 #8 step 1: the copy says
// outright that the Playbook is unfinished and nothing has been emailed, and
// drops "vetted recommendations only", which implied a recommendations list
// that does not exist.
//
// Primary CTA is the live outbound hop (/go/1800accountant), styled with
// the same -dark accent tokens as the rest of this dark-chrome surface
// (PALETTE.md) and the same link contract as AffiliateCtaButton /
// MidArticleCta's "hop" variant (app/(public)/reviews/[slug]/page.tsx,
// components/public/MidArticleCta.tsx): target=_blank,
// rel="sponsored nofollow noopener noreferrer", hrefLang, aria-label mirroring the
// visible text. Money Skills hub is a secondary, clearly-subordinate link —
// internal navigation, no target=_blank.

import Link from "next/link";
import { ArrowRight, ExternalLink, Check } from "lucide-react";
import GoPrefetchAnchor from "@/components/public/GoPrefetchAnchor";

export default function PlaybookThankYou() {
  return (
    <div className="relative">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-content-elevated border border-content-border">
        <Check size={24} className="text-forge-success" />
      </div>
      <h3 className="text-xl font-bold text-content-text mb-2">
        You&apos;re on the list
      </h3>
      <p className="text-content-muted text-base max-w-md mx-auto mb-6">
        We saved your email. The Buyer&apos;s Playbook isn&apos;t finished yet.
        When it is, we&apos;ll email it to you, and
        we&apos;ll only email you about the Playbook.
      </p>

      <GoPrefetchAnchor
        href="/go/1800accountant"
        target="_blank"
        rel="sponsored nofollow noopener noreferrer"
        hrefLang="en"
        aria-label="See 1800Accountant plans & pricing (opens in new tab)"
        className="group inline-flex items-center justify-center gap-2 rounded-lg bg-forge-trust px-6 py-3.5 text-base font-semibold text-white transition-colors duration-300 hover:bg-forge-trust-hover"
      >
        See 1800Accountant plans &amp; pricing
        <ExternalLink size={16} className="transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
      </GoPrefetchAnchor>

      <p className="mt-4 text-base text-content-muted">
        Not ready for that?{" "}
        <Link
          href="/categories/money-skills"
          className="underline decoration-content-muted/40 underline-offset-2 hover:text-content-text hover:decoration-content-text/60 transition-colors inline-flex items-center gap-1"
        >
          Browse the Money Skills hub
          <ArrowRight size={12} aria-hidden="true" />
        </Link>
      </p>
    </div>
  );
}
